/**
 * DiscrepancyDetail — Type 2 full-bleed detail page for a single discrepancy.
 * Route: /purchasing/discrepancies/:id where :id is receiving_line_items.id.
 *
 * Layout copies PODetail.tsx (Day 50): outer flex-col h-full -m-4, fixed
 * toolbar with back nav + status pill + action buttons, scrollable two-column
 * content (flex-1 left + sticky w-72 right). Closes BACKLOG B26 by hosting
 * <NotesPanel entityType="discrepancy_resolution" /> for ongoing annotation
 * once a resolution exists.
 *
 * Photos attach to the LINE ITEM (the durable thing), not the resolution
 * (which can be re-resolved). Receiving history is PO-scoped, current
 * record highlighted.
 */

import { useState, useEffect, useCallback, type JSX } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import NotesPanel from '../../components/notes/NotesPanel';
import PhotoCapture from '../../components/photos/PhotoCapture';
import PhotoGallery from '../../components/photos/PhotoGallery';
import ResolveDiscrepancyModal, {
  CONDITION_BADGES,
  RESOLUTION_BADGES,
} from '../../components/purchasing/ResolveDiscrepancyModal';
import { fetchReceivingHistory, type ReceivingHistoryRow } from '../../lib/receiving';
import { Toast, type ToastState } from '../../components/ui/Toast';
import { extractEnvelope } from '../../lib/extractEnvelope';
import type { PostgrestError } from '@supabase/supabase-js';

// Day 84.6 P4: error type widened to PostgrestError | null to compose with
// extractEnvelope Path 1 translation.
type LooseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;

function fmt$(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface DetailRow {
  id: string;
  isg_sku: string;
  expected_qty: number;
  received_qty: number;
  condition: string;
  has_discrepancy: boolean;
  product_id: string | null;
  product_title: string | null;
  product_brand: string | null;
  cost_per_unit: number | null;
  receiving_record_id: string;
  received_at: string | null;
  received_by: string | null;
  received_by_name: string | null;
  po_id: string;
  po_number: string;
  po_status: string;
  fulfillment_path: string | null;
  distributor_id: string | null;
  distributor_name: string | null;
  resolution_id: string | null;
  resolution: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
}

interface RawJoinRow {
  id: string;
  isg_sku: string;
  expected_qty: number;
  received_qty: number;
  condition: string;
  has_discrepancy: boolean;
  product_id: string | null;
  receiving_record_id: string;
  po_line_item_id: string;
  products: { title: string | null; brand: string | null } | null;
  receiving_records: {
    id: string;
    received_at: string | null;
    received_by: string | null;
    purchase_orders: {
      id: string;
      po_number: string;
      status: string;
      fulfillment_path: string | null;
      distributor_id: string | null;
      distributors: { name: string | null } | null;
    } | null;
  } | null;
  po_line_items: { cost_per_unit: number | null } | null;
  discrepancy_resolutions:
    | {
        id: string;
        resolution: string;
        resolved_at: string | null;
        resolved_by: string | null;
      }[]
    | null;
}

export default function DiscrepancyDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Day 64.5 V-ROUI: viewer extends the existing isDistRep gate on both
  // Resolve trigger buttons (L371 toolbar primary + L626 quick-action card).
  const { isDistRep, isViewer } = useApp();

  const [row, setRow] = useState<DetailRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [history, setHistory] = useState<ReceivingHistoryRow[]>([]);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [photoRefreshKey, setPhotoRefreshKey] = useState(0);
  const [toast, setToast] = useState<ToastState | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    const { data, error } = await supabase
      .from('receiving_line_items')
      .select(
        `
        id, isg_sku, expected_qty, received_qty, condition, has_discrepancy,
        product_id, receiving_record_id, po_line_item_id,
        products(title, brand),
        receiving_records!inner(
          id, received_at, received_by,
          purchase_orders!inner(
            id, po_number, status, fulfillment_path, distributor_id,
            distributors(name)
          )
        ),
        po_line_items(cost_per_unit),
        discrepancy_resolutions(id, resolution, resolved_at, resolved_by)
        `
      )
      .eq('id', id)
      .maybeSingle();

    if (error) {
      setLoadError(error.message);
      setLoading(false);
      return;
    }
    if (!data) {
      setLoadError('Discrepancy not found');
      setLoading(false);
      return;
    }

    const r = data as unknown as RawJoinRow;
    const rec = r.receiving_records;
    const po = rec?.purchase_orders;
    const latestResolution =
      r.discrepancy_resolutions && r.discrepancy_resolutions.length > 0
        ? r.discrepancy_resolutions[0]
        : null;

    // Day 51 Phase 0 (B33): separate profile lookup. PostgREST embed
    // `profiles:received_by(full_name)` doesn't work — receiving_records
    // .received_by FKs to auth.users, not profiles, and PostgREST cannot
    // traverse via auth.users (not exposed in the API surface).
    let receivedByName: string | null = null;
    if (rec?.received_by) {
      const { data: prof } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', rec.received_by)
        .maybeSingle();
      receivedByName = prof?.full_name ?? null;
    }

    const detail: DetailRow = {
      id: r.id,
      isg_sku: r.isg_sku,
      expected_qty: r.expected_qty,
      received_qty: r.received_qty,
      condition: r.condition,
      has_discrepancy: r.has_discrepancy,
      product_id: r.product_id,
      product_title: r.products?.title ?? null,
      product_brand: r.products?.brand ?? null,
      cost_per_unit: r.po_line_items?.cost_per_unit ?? null,
      receiving_record_id: r.receiving_record_id,
      received_at: rec?.received_at ?? null,
      received_by: rec?.received_by ?? null,
      received_by_name: receivedByName,
      po_id: po?.id ?? '',
      po_number: po?.po_number ?? '—',
      po_status: po?.status ?? '',
      fulfillment_path: po?.fulfillment_path ?? null,
      distributor_id: po?.distributor_id ?? null,
      distributor_name: po?.distributors?.name ?? null,
      resolution_id: latestResolution?.id ?? null,
      resolution: latestResolution?.resolution ?? null,
      resolved_at: latestResolution?.resolved_at ?? null,
      resolved_by: latestResolution?.resolved_by ?? null,
    };
    setRow(detail);

    if (po?.id) {
      const hist = await fetchReceivingHistory(po.id);
      setHistory(hist);
    }

    setLoading(false);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleResolve = async (resolution: string, notes: string): Promise<void> => {
    if (!row) return;
    setResolving(true);
    try {
      // Day 84.6 P4.2.a — resolve_discrepancy + create_entity_note envelope-extract
      // per Decision 45 + Path B audit 🔴 silent-broken fix. Pre-Phase-1 returned
      // resolution uuid; post-migration returns { ok, data: { resolution_id }, userErrors }
      // jsonb envelope. The `resolutionId as string` cast was writing envelope
      // object as entity_notes FK — corrupted writes silently.
      const resolveResult = await callRpc('resolve_discrepancy', {
        p_line_item_id: row.id,
        p_resolution: resolution,
      });
      const resolveEnv = extractEnvelope<{ resolution_id: string }>(resolveResult);
      if (!resolveEnv.isOk) {
        throw new Error(resolveEnv.userErrors[0]?.message ?? 'Resolve failed');
      }
      const resolutionId = resolveEnv.data!.resolution_id;
      if (notes.trim() && resolutionId) {
        const noteResult = await callRpc('create_entity_note', {
          p_entity_type: 'discrepancy_resolution',
          p_entity_id: resolutionId,
          p_text: notes.trim(),
          p_visibility: 'all_internal',
        });
        const noteEnv = extractEnvelope<{ id: string }>(noteResult);
        if (!noteEnv.isOk) {
          // Note write failure is non-blocking — log + continue (resolution succeeded)
          console.warn('[DiscrepancyDetail] note write failed:', noteEnv.userErrors);
        }
      }
      setToast({ kind: 'success', message: 'Discrepancy resolved' });
      setResolveOpen(false);
      await load();
    } catch (e) {
      setToast({
        kind: 'error',
        message: `Failed to resolve: ${e instanceof Error ? e.message : String(e)}`,
      });
    } finally {
      setResolving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-sm text-ink-muted">Loading discrepancy…</span>
      </div>
    );
  }

  if (loadError || !row) {
    return (
      <div className="flex flex-col h-full -m-4">
        <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
          <button
            onClick={() => navigate('/purchasing/discrepancies')}
            className="flex items-center gap-1 text-xs text-ink-muted hover:text-ink-primary transition-colors mb-1 cursor-pointer"
          >
            <ChevronLeft size={12} /> Back to Discrepancies
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle size={32} className="text-danger mx-auto mb-2" />
            <p className="text-sm text-ink-primary">{loadError || 'Discrepancy not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const conditionBadge = CONDITION_BADGES[row.condition] || {
    label: row.condition,
    cls: 'bg-surface-4 text-ink-muted',
  };

  const resolutionBadge = row.resolution
    ? RESOLUTION_BADGES[row.resolution] || {
        label: row.resolution,
        cls: 'bg-surface-4 text-ink-muted',
      }
    : null;

  const qtyDelta = row.received_qty - row.expected_qty;
  const valueImpact = row.cost_per_unit != null ? Math.abs(qtyDelta) * row.cost_per_unit : null;

  const isResolved = !!row.resolution;

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      <ResolveDiscrepancyModal
        open={resolveOpen}
        item={
          resolveOpen
            ? {
                isg_sku: row.isg_sku,
                expected_qty: row.expected_qty,
                received_qty: row.received_qty,
                condition: row.condition,
              }
            : null
        }
        onConfirm={handleResolve}
        onCancel={() => setResolveOpen(false)}
        submitting={resolving}
      />

      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <button
          onClick={() => navigate('/purchasing/discrepancies')}
          className="flex items-center gap-1 text-xs text-ink-muted hover:text-ink-primary transition-colors mb-1 cursor-pointer"
        >
          <ChevronLeft size={12} /> Back to Discrepancies
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">
              Discrepancy — <span className="font-mono">{row.isg_sku}</span>
            </h1>
            {isResolved && resolutionBadge ? (
              <span
                className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', resolutionBadge.cls)}
              >
                Resolved · {resolutionBadge.label}
              </span>
            ) : (
              <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-warning/15 text-warning">
                Unresolved
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isResolved && !isDistRep && !isViewer && (
              <button onClick={() => setResolveOpen(true)} className="arbi-btn-primary text-xs">
                Resolve
              </button>
            )}
            <button
              disabled
              className="arbi-btn-ghost text-xs opacity-50 cursor-not-allowed"
              title="Reassign — Phase 4"
            >
              Reassign
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex gap-4">
          {/* Left: details, photos, notes */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Discrepancy Info */}
            <div className="bg-surface-1 border border-border rounded-lg p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-3">Discrepancy Info</h2>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
                <div>
                  <span className="text-ink-muted">Product</span>
                  <p className="text-ink-primary truncate">{row.product_title || '—'}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Brand</span>
                  <p className="text-ink-primary">{row.product_brand || '—'}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Expected Qty</span>
                  <p className="font-mono text-ink-bright">
                    {qtyDelta < 0 ? (
                      <>
                        <span className="line-through text-danger">{row.expected_qty}</span>{' '}
                      </>
                    ) : (
                      row.expected_qty
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-ink-muted">Received Qty</span>
                  <p className="font-mono text-ink-bright">
                    {qtyDelta > 0 ? (
                      <>
                        <span className="line-through text-danger">{row.received_qty}</span>{' '}
                      </>
                    ) : (
                      row.received_qty
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-ink-muted">Condition</span>
                  <p>
                    <span
                      className={clsx(
                        'px-1.5 py-0.5 rounded text-2xs font-medium',
                        conditionBadge.cls
                      )}
                    >
                      {conditionBadge.label}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-ink-muted">Received At</span>
                  <p className="font-mono text-ink-secondary">{fmtDateTime(row.received_at)}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Received By</span>
                  <p className="text-ink-primary">{row.received_by_name || '—'}</p>
                </div>
              </div>
            </div>

            {/* PO Info */}
            <div className="bg-surface-1 border border-border rounded-lg p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-3">PO Info</h2>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
                <div>
                  <span className="text-ink-muted">PO Number</span>
                  <p>
                    <button
                      onClick={() => navigate(`/purchasing/purchase-orders/${row.po_id}`)}
                      className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
                    >
                      {row.po_number}
                    </button>
                  </p>
                </div>
                <div>
                  <span className="text-ink-muted">Distributor</span>
                  <p className="text-ink-primary">{row.distributor_name || '—'}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Fulfillment Path</span>
                  <p className="text-ink-primary">{row.fulfillment_path || '—'}</p>
                </div>
                <div>
                  <span className="text-ink-muted">PO Status</span>
                  <p className="text-ink-primary">{row.po_status}</p>
                </div>
              </div>
            </div>

            {/* Receiving History */}
            <div className="bg-surface-1 border border-border rounded-lg p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-3">Receiving History</h2>
              {history.length === 0 ? (
                <p className="text-xs text-ink-muted">No receiving records yet</p>
              ) : (
                <ul className="space-y-1.5">
                  {history.map((h) => {
                    const isCurrent = h.id === row.receiving_record_id;
                    return (
                      <li key={h.id}>
                        <button
                          type="button"
                          onClick={() => navigate(`/purchasing/receiving/${row.po_id}`)}
                          className={clsx(
                            'w-full flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-surface-2 transition-colors text-left',
                            isCurrent && 'bg-accent-muted/20 border border-accent/40'
                          )}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="font-mono text-ink-secondary whitespace-nowrap">
                              {fmtDateTime(h.received_at)}
                            </span>
                            <span className="text-ink-primary truncate">
                              {h.received_by_name || 'Unknown'}
                            </span>
                            {isCurrent && (
                              <span className="text-2xs text-accent font-medium">current</span>
                            )}
                          </div>
                          <span className="text-ink-muted font-mono whitespace-nowrap">
                            {h.line_item_count} item{h.line_item_count !== 1 ? 's' : ''}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Photos */}
            <div className="bg-surface-1 border border-border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-ink-bright">Photos</h2>
                <PhotoCapture
                  entityType="receiving_line_item"
                  entityId={row.id}
                  onUploaded={() => setPhotoRefreshKey((k) => k + 1)}
                  readOnly={isDistRep}
                />
              </div>
              <PhotoGallery
                entityType="receiving_line_item"
                entityId={row.id}
                refreshKey={photoRefreshKey}
                readOnly={isDistRep}
              />
            </div>

            {/* Notes — closes B26 */}
            {row.resolution_id ? (
              <NotesPanel entityType="discrepancy_resolution" entityId={row.resolution_id} />
            ) : (
              <div className="bg-surface-1 border border-border rounded-lg p-4">
                <h2 className="text-sm font-semibold text-ink-bright mb-2">Notes</h2>
                <p className="text-xs text-ink-muted">Notes will be available after resolution.</p>
              </div>
            )}

            {/* Resolution History */}
            {isResolved && (
              <div className="bg-surface-1 border border-border rounded-lg p-4">
                <h2 className="text-sm font-semibold text-ink-bright mb-3">Resolution History</h2>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
                  <div>
                    <span className="text-ink-muted">Resolution</span>
                    <p>
                      {resolutionBadge && (
                        <span
                          className={clsx(
                            'px-1.5 py-0.5 rounded text-2xs font-medium',
                            resolutionBadge.cls
                          )}
                        >
                          {resolutionBadge.label}
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-ink-muted">Resolved At</span>
                    <p className="font-mono text-ink-secondary">{fmtDateTime(row.resolved_at)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: status + quick actions */}
          <div className="w-72 flex-shrink-0">
            <div className="sticky top-0 space-y-4">
              <div className="bg-surface-1 border border-border rounded-lg p-4">
                <h2 className="text-sm font-semibold text-ink-bright mb-3">Status</h2>
                <div className="space-y-3">
                  <div>
                    {isResolved && resolutionBadge ? (
                      <span
                        className={clsx(
                          'inline-block px-2 py-1 rounded text-xs font-medium',
                          resolutionBadge.cls
                        )}
                      >
                        Resolved · {resolutionBadge.label}
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-warning/15 text-warning">
                        Unresolved
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Qty Delta</span>
                    <span
                      className={clsx(
                        'font-mono',
                        qtyDelta < 0 ? 'text-danger' : 'text-ink-bright'
                      )}
                    >
                      {qtyDelta > 0 ? '+' : ''}
                      {qtyDelta} units
                    </span>
                  </div>
                  {!isDistRep && valueImpact != null && (
                    <div className="flex justify-between text-xs">
                      <span className="text-ink-muted">Value Impact</span>
                      <span className="font-mono text-ink-bright">{fmt$(valueImpact)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-surface-1 border border-border rounded-lg p-4">
                <h2 className="text-sm font-semibold text-ink-bright mb-3">Quick Actions</h2>
                <div className="space-y-2">
                  <button
                    onClick={() => setResolveOpen(true)}
                    disabled={isResolved || isDistRep || isViewer}
                    className={clsx(
                      'arbi-btn-primary text-xs w-full',
                      (isResolved || isDistRep || isViewer) && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {isResolved ? 'Already Resolved' : 'Resolve'}
                  </button>
                  <button
                    disabled
                    className="arbi-btn-ghost text-xs w-full opacity-50 cursor-not-allowed"
                    title="Reassign — Phase 4"
                  >
                    Mark for Reassignment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
